import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(null)
  const [equipeRole, setEquipeRole] = useState(null)
  const prevUserId = useRef(null)
  const roleCheckInFlight = useRef(false)

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false)
      return
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null
      setUser(u)
      prevUserId.current = u?.id ?? null
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = session?.user ?? null
      setUser(newUser)

      // Only reset role cache when user ACTUALLY changes (login/logout), not on token refresh
      const newId = newUser?.id ?? null
      if (newId !== prevUserId.current) {
        prevUserId.current = newId
        setIsAdmin(null)
        setEquipeRole(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const checkIsAdmin = useCallback(async () => {
    if (isAdmin !== null && equipeRole !== null) return isAdmin
    if (roleCheckInFlight.current) return isAdmin ?? false
    if (!user) return false

    roleCheckInFlight.current = true
    try {
      const { data: role } = await supabase.rpc('get_user_role')
      setEquipeRole(role || null)

      if (role === 'fondateur' || role === 'admin') {
        setIsAdmin(true)
        return true
      }

      if (role === 'commercial') {
        setIsAdmin(false)
        return false
      }

      // Fallback: check is_admin on merchants table
      const { data: isMerchantAdmin } = await supabase.rpc('is_admin')
      const result = isMerchantAdmin === true
      setIsAdmin(result)
      return result
    } catch {
      setIsAdmin(false)
      setEquipeRole(null)
      return false
    } finally {
      roleCheckInFlight.current = false
    }
  }, [user, isAdmin, equipeRole])

  const signUp = (email, password) =>
    supabase.auth.signUp({ email, password })

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password })

  const signOut = () => supabase.auth.signOut()

  const value = useMemo(
    () => ({ user, loading, isAdmin, equipeRole, checkIsAdmin, signUp, signIn, signOut }),
    [user, loading, isAdmin, equipeRole, checkIsAdmin]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
